import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { useAppSelector } from '@/store/redux';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import type { RegisterCredentials } from '@/types/auth';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input } from '@/components/ui';
import { toast } from 'react-hot-toast';
import { margin } from '@mui/system';


// Styled components matching Regions design
const RegisterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const BRAND_COLOR = '#17A6DA';

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 50%',
  padding: theme.spacing(2, 6),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: 'white',
  [theme.breakpoints.down('md')]: {
    flex: '1',
    padding: theme.spacing(4, 3),
    margin: 'auto',
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 50%',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    display: 'none', // ✅ hide completely
  },
}));

// Register Banner styled components
const BannerContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  //background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
}));

const LogoHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(3),
  left: theme.spacing(4),
}));

const BannerImage = styled('img')(() => ({
  width: '100%',
  height: '100%',
  objectPosition: 'cover',
}));

const BrandHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    textAlign: 'center',
  },
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const FormSection = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const FormCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  padding: theme.spacing(6, 4),
  borderRadius: '12px',
  boxShadow: `
  0 8px 16px rgba(0,0,0,0.08),
  0 20px 40px rgba(0,0,0,0.15)
`,
  border: '1px solid #f0f0f0',
}));


const inputSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent !important',
  },

  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent !important',
    borderRadius: '8px',

    '& fieldset': {
      borderColor: '#d0d5dd',
    },

    '&:hover fieldset': {
      borderColor: '#b0b7c3',
    },

    '&.Mui-focused fieldset': {
      borderColor: ' #808080',
      borderWidth: '2px',
    },
  },

  // Remove browser autofill bg
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px transparent inset',
    WebkitTextFillColor: '#000',
    transition: 'background-color 9999s ease-in-out 0s',
  },

  // Label colors
  '& .MuiInputLabel-root': {
    color: '#667085',
  },

  '& .MuiInputLabel-root.Mui-focused': {
    color: BRAND_COLOR,
  },
};

const FooterLinks = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

// Validation schema
const registerSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      'Please enter a valid email address'
    ),
  name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters'),
  mobile_number: yup
    .string()
    .required('Mobile number is required')
    .matches(
      /^(?!\+?91)(?!0+)(?!.*(\d)\1{9})[6-9]\d{9}$/,
      'Enter a valid 10-digit mobile number without country code'
    ),

  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    ),
  confirm_password: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
});

// Initial form values
const initialValues: RegisterCredentials = {
  email: '',
  name: '',
  mobile_number: '',
  password: '',
  confirm_password: '',
  role: 'merchant',
};

export const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const { register, loading } = useAuth();
  const authState = useAppSelector((state) => state.auth);
  const [registrationError, setRegistrationError] = useState<string | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);



  // Handle form submission
  const handleSubmit = async (
    values: RegisterCredentials,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {

    setRegistrationError(null); // Clear any previous errors

    try {
      await register(values);

      if (!authState.error) {
        //toast.dismiss(loadingToast);
        //navigate('/auth/login', { replace: true });
      } else {
        setRegistrationError(authState.error);
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      toast.error(errorMessage, {
        position: 'top-center',
        style: {
          marginTop: '50vh',
          transform: 'translateY(-50%)'
        }
      });
      setRegistrationError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RegisterContainer>
      {/* Left Panel - Register Form */}
      <LogoHeader>
        <img
          src="/logos/logo-white_1.png"
          alt="GrowthBnk Logo"
          style={{ width: '150px', objectFit: 'contain' }}
        />
      </LogoHeader>
      <LeftPanel>
        {/* Brand Header - Regions Style */}
        <FormSection>
          {/* Welcome Section - Regions Style */}
          <WelcomeSection>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', sm: 'flex-center' },
              paddingBottom: 1,
            }}>
            </Box>
            <Typography
              variant="h1"
              weight="bold"
              textAlign={'center'}
              sx={{ fontSize: { xs: '1.00rem', sm: '1.5rem' }, color: 'rgb(40, 42, 48)', }}
            >
              Create Account
            </Typography>
            <Typography variant="body2" color="textSecondary" textAlign={'center'} sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              Join GrowthBnk and start managing your business.
            </Typography>
          </WelcomeSection>

          {/* Register Form */}
          <Box
            sx={{
              width: {
                xs: '320px',   // Mobile devices
                sm: '420px',   // Tablets
                md: '700px',   // Desktops
                lg: '80%',    // small screens: full width
              },
              maxWidth: '100%',
              margin: '0 auto',
            }}
          >
            <FormCard>
              <Formik
                initialValues={initialValues}
                validationSchema={registerSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
                  <Form>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* Email Field */}
                      <Box sx={{ padding: 0 }}>
                        <Input
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          placeholder="Enter your email address"
                          value={values.email}
                          onChange={e => setFieldValue('email', e.target.value)}
                          onBlur={() => setFieldTouched('email', true)}
                          onFocus={() => {
                            if (!touched.email) {
                              setFieldTouched('email', true);
                            }
                          }}
                          error={touched.email && !!errors.email}
                          helperText={touched.email && errors.email}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          sx={inputSx}
                        />
                      </Box>

                      {/* Company Name Field */}
                      <Box sx={{ padding: 0 }}>
                        <Input
                          fullWidth
                          label="Name"
                          name="name"
                          type="text"
                          placeholder="Enter your name"
                          value={values.name}
                          onChange={e => setFieldValue('name', e.target.value)}
                          onBlur={() => setFieldTouched('name', true)}
                          onFocus={() => {
                            if (!touched.name) {
                              setFieldTouched('name', true);
                            }
                          }}
                          error={touched.name && !!errors.name}
                          helperText={touched.name && errors.name}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          sx={inputSx}
                        />
                      </Box>

                      {/* Mobile Number Field */}
                      <Box sx={{ padding: 0 }}>
                        <Input
                          fullWidth
                          label="Mobile Number"
                          name="mobile_Number"
                          type="tel"
                          placeholder="Enter your mobile number"
                          value={values.mobile_number}
                          onChange={e => setFieldValue('mobile_number', e.target.value)}
                          onBlur={() => setFieldTouched('mobile_number', true)}
                          onFocus={() => {
                            if (!touched.mobile_number) {
                              setFieldTouched('mobile_number', true);
                            }
                          }}
                          error={touched.mobile_number && !!errors.mobile_number}
                          helperText={touched.mobile_number && errors.mobile_number}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          sx={inputSx}
                        />
                      </Box>

                      {/* Password Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Password"
                          name="password"
                          type="password"
                          placeholder="Create a strong password"
                          value={values.password}
                          onChange={e => setFieldValue('password', e.target.value)}
                          onBlur={() => setFieldTouched('password', true)}
                          onFocus={() => {
                            if (!touched.password) {
                              setFieldTouched('password', true);
                            }
                          }}
                          error={touched.password && !!errors.password}
                          helperText={touched.password && errors.password}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          showPassword={showPassword}
                          onPasswordToggle={() => setShowPassword(!showPassword)}
                          sx={inputSx}
                        />
                      </Box>

                      {/* Confirm Password Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Confirm Password"
                          name="confirm_password"
                          type="password"
                          placeholder="Confirm your password"
                          value={values.confirm_password}
                          onChange={e => setFieldValue('confirm_password', e.target.value)}
                          onBlur={() => setFieldTouched('confirm_password', true)}
                          onFocus={() => {
                            if (!touched.confirm_password) {
                              setFieldTouched('confirm_password', true);
                            }
                          }}
                          error={touched.confirm_password && !!errors.confirm_password}
                          helperText={touched.confirm_password && errors.confirm_password}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          showPassword={showConfirmPassword}
                          onPasswordToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                          sx={inputSx}
                        />
                      </Box>

                      {/* Register Button */}
                      <Box sx={{ width: '100%' }}>
                        <Button
                          type="submit"
                          variant="primary"
                          size="small"
                          fullWidth
                          loading={loading}
                          loadingText="Creating account..."
                          disabled={
                            !values.email ||
                            !values.name ||
                            !values.mobile_number ||
                            !values.password ||
                            !values.confirm_password
                          }
                          sx={{
                            backgroundColor: BRAND_COLOR,
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            height: '50px',
                            '&:hover': {
                              backgroundColor: '#1392C1', // slightly darker
                            },
                            '&:disabled': {
                              backgroundColor: '#9bd3ea',
                              color: '#fff',
                            },
                          }}
                        >
                          Create Account
                        </Button>
                        {registrationError && (
                          <Typography
                            color="error"
                            variant="body2"
                            sx={{
                              mt: 1,
                              textAlign: 'center',
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                              padding: '8px',
                              borderRadius: '4px',
                            }}
                          >
                            {registrationError}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Form>
                )}
              </Formik>
            </FormCard>
          </Box>

          <FooterLinks>
            <Link
              component={RouterLink}
              to="/auth/login"
              variant="body2"
              sx={{
                color: 'rgb(40, 42, 48)',
                textDecoration: 'none',
                fontSize: '0.75rem',
                fontWeight: '700',
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            >
              Merchant Login?
            </Link>
          </FooterLinks>

          {/* Footer Links */}
          {/* <FooterLinks>
            <Typography variant="body2" color="textSecondary">
              By creating an account, you agree to our{' '}
              <Link
                href="#"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                href="#"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Privacy Policy
              </Link>
            </Typography>
          </FooterLinks> */}
        </FormSection>
      </LeftPanel>

      {/* Right Panel - Register Banner */}
      <RightPanel>
        <BannerContainer>
          <BannerImage src="/banner.svg" alt="Register Banner - Your Dream Home" />
        </BannerContainer>
      </RightPanel>
    </RegisterContainer>
  );
};

export default RegisterForm;